import React from 'react';
import styled from '@emotion/styled';
import Icon from '../../../Components/Icon';
import editIcon from '../../../Assets/Icons/edit.svg';
import trashIcon from '../../../Assets/Icons/trash.svg';
import { LocalItem, CartItem } from '~Types';
import { useCurrency } from '~Hooks';

const Wrapper = styled.div`
  position: relative;
  border-bottom: solid 1px rgb(238, 238, 241);
`;

const NameWrapper = styled.div<{ bottomOffset: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 24px 0 ${({ bottomOffset }) => (bottomOffset ? '4px' : '25px')} 0;
`;

const Name = styled.h3<{ selected: boolean }>`
  margin: 0;
  color: ${({ selected }) => (selected ? '#cccccc' : 'rgb(33, 33, 36)')};
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  transition: 0.3s color, 0.3s opacity;
`;

const Price = styled.p<{ selected: boolean }>`
  margin: 0;
  color: ${({ selected }) => (selected ? '#cccccc' : 'rgb(23, 23, 26)')};
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  line-height: 28px;
  text-align: right;
`;

const Variations = styled.div`
  padding: 4px 0 21px 16px;
`;

const Variation = styled.div`
  display: flex;
`;

const VariationName = styled.strong<{ removed?: boolean }>`
  flex: 1;
  padding: 0 10px 0 0;
  color: ${({ removed }) => (removed ? '#cc3333' : 'rgb(97, 97, 100)')};
  font-weight: normal;
  font-family: Eina01-Regular;
  font-size: 16px;
  line-height: 24px;
`;

const VariationPrice = styled.strong`
  color: rgb(97, 97, 100);
  font-family: Eina01-Regular;
  font-size: 16px;
  font-weight: normal;
  line-height: 24px;
  text-align: right;
`;

const EditButtons = styled.div<{ visible?: boolean }>`
  display: flex;
  position: absolute;
  right: 0;
  top: 9px;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: 0.3s visibility, 0.3s opacity;
`;

export const RoundButton = styled.div<{ color?: string }>`
  height: 63px;
  width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ color }) => (color ? color : 'rgb(238, 238, 241)')};
  border-radius: 24px;
  margin: 0 0 0 9px;
`;

interface Props {
  item: LocalItem;
  isOffer?: boolean;
  selected: boolean;
  onSelectItem: (id: string) => void;
  onDeleteItemFromOrder: (id: string) => void;
  takeAwayBagId?: string;
  entry: CartItem;
  onClickItem: (item: LocalItem) => void;
  onShowTakeAwayBagAlert?: (value: boolean) => void;
}

const Item = ({
  item,
  selected,
  onSelectItem,
  onDeleteItemFromOrder,
  takeAwayBagId,
  entry,
  onClickItem,
  onShowTakeAwayBagAlert,
}: Props) => {
  const currency = useCurrency();
  const deleteColor = item.itemId !== takeAwayBagId ? '#e3273b' : '#565d64';

  const onDelete = () => {
    // Do not allow the bag to be removed
    if (item.itemId === takeAwayBagId) {
      onShowTakeAwayBagAlert && onShowTakeAwayBagAlert(true);
      return;
    }

    onDeleteItemFromOrder(item.id);
  };

  return (
    <Wrapper onClick={() => onSelectItem(item.id)}>
      <NameWrapper bottomOffset={!!item.variations.length}>
        <EditButtons visible={selected}>
          {item.variations.length ? (
            <RoundButton onClick={() => onClickItem(item)}>
              <Icon icon={editIcon} color={'#565d64'} size={30} />
            </RoundButton>
          ) : null}
          <RoundButton color={deleteColor} onClick={onDelete}>
            <Icon icon={trashIcon} color={'#ffffff'} size={30} />
          </RoundButton>
        </EditButtons>
        <Name selected={selected}>{item.title}</Name>
        <Price selected={selected}>{currency.format(entry.amount! - entry.discount!)}</Price>
      </NameWrapper>

      {item.variations.length || item.removedVariations.length ? (
        <Variations>
          {item.variations.map((variation) => (
            <Variation key={variation.id}>
              <VariationName>{variation.name}</VariationName>
              {variation.price !== 0 && (
                <VariationPrice>{currency.format(variation.price)}</VariationPrice>
              )}
            </Variation>
          ))}
          {item.removedVariations.map((variation) => (
            <Variation key={variation.id}>
              <VariationName removed>- {variation.name}</VariationName>
              {variation.price !== 0 && (
                <VariationPrice>{currency.format(variation.price)}</VariationPrice>
              )}
            </Variation>
          ))}
        </Variations>
      ) : null}
    </Wrapper>
  );
};

export default Item;
